import React, {
  createContext,
  useState,
  ReactNode,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import axios from 'axios';
import { apiRoute } from '../constants/api-constants';
import Cookies from 'universal-cookie';
import { useNavigate } from 'react-router-dom';
import { User } from '../interfaces/user-interfaces';
import { Onboarding } from '../interfaces/onboarding-interfaces';
import { resolvePostAuthRoute, isPublicPath } from '../utils/post-auth-route';
import toast from '../utils/toast';
import {
  ORG_SUSPENDED_CODE,
  ORG_SUSPENDED_EVENT,
  ACCOUNT_CLOSING_CODE,
  ACCOUNT_CLOSING_EVENT,
  SESSION_ENDED_EVENT,
} from '../axios-setup';

// Auth lives in httpOnly cookies now, so the client CANNOT look at the token to
// guess whether it is signed in. That makes an explicit tri-state necessary:
// 'loading' until /auth answers, then 'authed' or 'anon'. Guards render a
// placeholder while loading instead of flashing a redirect to login.
export type AuthStatus = 'loading' | 'authed' | 'anon';

interface AuthContextType {
  setUser: (user: User | null | undefined) => void;
  user: User | null | undefined;
  onboarding: Onboarding | null;
  setOnboarding: (onboarding: Onboarding | null) => void;
  // The signed-in user's permission set, authoritative from the server. Drives
  // the shared can()/Can helpers so UI visibility matches backend enforcement.
  permissions: string[];
  setPermissions: (permissions: string[]) => void;
  authStatus: AuthStatus;
  // The server has answered that this person's organisation is suspended.
  // While it is true the app shows one screen saying so (App.tsx) and keeps
  // the session: a suspension is lifted by Lobbero, not by signing in again.
  orgSuspended: boolean;
  // Independent Stays IS8: the owner closed this account (frozen). The app
  // shows the closing screen (App.tsx), which offers the owner Reopen and
  // clears this once the account is open again.
  accountClosing: boolean;
  setAccountClosing: (closing: boolean) => void;
  // Adopt a freshly authenticated session in ONE call. Use this after login,
  // signup and invite-accept rather than calling the individual setters.
  //
  // Setting user/onboarding/permissions without also moving authStatus to
  // 'authed' produces a redirect loop that is easy to miss: RootRouter sends a
  // truthy `user` on a public path forward to the dashboard, while its
  // `authStatus === 'anon'` rule sends any non-'/' path back to login. Each
  // guard then re-triggers the other indefinitely.
  signIn: (
    user: User,
    onboarding: Onboarding | null,
    permissions: string[]
  ) => void;
  // Single logout path for the whole app. `everywhere` also revokes every other
  // device (and bumps token_version server-side).
  logout: (everywhere?: boolean, opts?: { sessionEnded?: boolean }) => Promise<void>;
  // Re-read GET /auth and adopt the answer, WITHOUT changing identity.
  //
  // This provider's state is a CACHE of that endpoint (see the note above), and
  // a cache has to be invalidated by whatever changed the underlying record. The
  // case that made this necessary: confirming your email address writes
  // `email_verified_at` server-side, but nothing in the browser had any reason
  // to re-ask, so the session went on reporting the account as unverified until
  // the next full reload — including on the very page that had just verified it.
  //
  // Distinct from signIn(), which ADOPTS A DIFFERENT IDENTITY and announces that
  // to every other tab. This is the same person with a changed record, so it
  // deliberately does not announce: a broadcast would make every other tab
  // re-probe for a change they will pick up on their own next probe anyway, and
  // the two-tab echo that caused is exactly what the resync listener below was
  // written to avoid.
  refreshAuth: () => Promise<void>;
  // Revoke this browser's session and drop local auth state, WITHOUT navigating.
  //
  // The handoff pages (/invite/:token, /reset-password) need exactly this and
  // nothing more: they must not leave the incumbent session live while they mint
  // a different identity, and they must not be thrown to '/' in the middle of
  // doing it — which is what logout() would do. Everything else should keep
  // calling logout().
  endSession: () => Promise<void>;
  cookies: Cookies;
}

/** What a person is told when their session ends without their doing. */
export const SESSION_ENDED_NOTICE =
  'You have been signed out. Sign in again to continue.';

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}


/**
 * Drop this browser's push subscription and forget it server-side.
 *
 * Swallows everything: no browser support, no service worker, no subscription,
 * a failed DELETE — none of them is a reason to keep somebody signed in.
 */
const releasePushSubscription = async (): Promise<void> => {
  try {
    if (!('serviceWorker' in navigator)) return;

    const registration = await navigator.serviceWorker.getRegistration(
      '/push-sw.js'
    );
    const subscription = await registration?.pushManager.getSubscription();

    if (!subscription) return;

    // UNSUBSCRIBE LOCALLY, AND LET THE SERVER LEARN IT THE WAY IT ALREADY
    // KNOWS HOW.
    //
    // There is no delete-by-endpoint route: DELETE
    // /notifications/push/subscriptions/:id takes a row id, and the list
    // endpoint does not return endpoints, so the client cannot match this
    // browser's subscription to a row. Guessing one would be worse than not
    // trying.
    //
    // It does not need to. Once the browser unsubscribes, the endpoint is dead,
    // and the very next send to it comes back 404/410 — which push.js already
    // treats as "gone for good" and disables the row
    // (lobbero_be/src/notifications/push.js:152). Delivery to this device stops
    // immediately, which is the part the person standing at the desk cares
    // about, and the row cleans itself up.
    await subscription.unsubscribe();
  } catch {
    // Never block a sign-out on notification plumbing.
  }
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null | undefined>(null);
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const [orgSuspended, setOrgSuspended] = useState(false);
  const [accountClosing, setAccountClosing] = useState(false);
  // Whether somebody is signed in RIGHT NOW, for a listener that must not
  // re-subscribe on every change of user (the session-ended notice below).
  const signedInRef = useRef(false);
  signedInRef.current = authStatus === 'authed' && !!user;
  const cookies = useMemo(() => new Cookies(), []);
  const navigate = useNavigate();

  const clearAuth = useCallback(() => {
    setUser(null);
    setOnboarding(null);
    setPermissions([]);
    setAuthStatus('anon');
    setOrgSuspended(false);
    // At once, not at the next render: a request still in flight when somebody
    // signs out on purpose must not earn them "you have been signed out".
    signedInRef.current = false;
  }, []);

  // ONE SESSION, ONE SOURCE OF TRUTH, ACROSS EVERY TAB.
  //
  // The session lives in httpOnly cookies and a server-side row; this provider's
  // state is only a CACHE of GET /auth. Cookies are shared by every tab of the
  // browser, so a session change in one tab silently invalidates what the others
  // are showing — a tab left on the dashboard keeps rendering a person who was
  // signed out in the tab next door, until it happens to make a request and gets
  // a 401. Two tabs then disagree about who is signed in, which is exactly what
  // an invite handoff or a password reset causes on purpose.
  //
  // So: any tab that CHANGES the session announces it, and every tab re-derives
  // its state from /auth. Nothing is copied between tabs — the announcement
  // carries no user data, only "go ask the server again". The server stays the
  // single source of truth and no tab ever trusts another tab's idea of who is
  // signed in.
  const authChannel = useMemo<BroadcastChannel | null>(
    () =>
      typeof BroadcastChannel === 'undefined'
        ? null
        : new BroadcastChannel('lobbero-auth'),
    []
  );

  const announceAuthChange = useCallback(() => {
    // localStorage is the fallback for browsers without BroadcastChannel: a
    // write fires a `storage` event in every OTHER tab of the same origin. The
    // value is a timestamp only so that consecutive writes always differ (a
    // repeated identical value fires nothing).
    if (authChannel) authChannel.postMessage('auth-changed');
    else {
      try {
        window.localStorage.setItem('lobbero-auth-ping', String(Date.now()));
      } catch {
        // Private mode / storage disabled. Cross-tab sync degrades to the 401
        // the next request would have produced anyway.
      }
    }
  }, [authChannel]);

  // One /auth probe at a time. Several announcements can land together (a tab
  // that signs out and straight back in as somebody else posts two), and each
  // probe costs a request plus a refresh attempt if the cookie is stale.
  const probing = useRef(false);

  const checkAuth = async () => {
    if (probing.current) return;
    probing.current = true;
    try {
      // No Authorization header: the httpOnly cookie rides along automatically.
      // A stale access token is refreshed-and-retried by the axios interceptor,
      // so reaching the catch means the session is genuinely over.
      const response = await axios.get(`${apiRoute}/auth` as string);

      const nextUser = response.data.user;
      const nextOnboarding: Onboarding | null =
        response.data.onboarding || null;
      setUser(nextUser);
      setOnboarding(nextOnboarding);
      setPermissions(response.data.permissions || []);
      setAuthStatus('authed');
      // An answer at all means the organisation is live (again).
      setOrgSuspended(false);
      // Independent Stays IS8: a frozen account's owner is let through to this
      // one read; open on the closing screen, not the dashboard.
      setAccountClosing(response.data.account_closing === true);

      // window.location, not the router's `location`: this function is also
      // called from cross-tab listeners that must not re-subscribe every time
      // the user navigates.
      const currentPath = window.location.pathname;

      // From the login screen, send everyone to their resolved home.
      if (currentPath === '/') {
        navigate(
          resolvePostAuthRoute(
            nextUser,
            nextOnboarding,
            response.data.permissions || []
          )
        );
      } else if (
        // Otherwise, keep an unfinished first-run inside the wizard — but never
        // fight a public route or an excursion the user is legitimately on.
        //
        // /account is exempt for the same reason as a public path: changing your
        // own password must not require having finished onboarding. Someone
        // whose invite-set password is already compromised, or who mistyped it
        // into a password manager, is stuck in the wizard until they can fix it.
        nextOnboarding &&
        !nextOnboarding.completed &&
        !currentPath.startsWith('/onboarding') &&
        !currentPath.startsWith('/account') &&
        !isPublicPath(currentPath)
      ) {
        navigate('/onboarding');
      }
    } catch (error) {
      // SUSPENDED IS NOT SIGNED OUT. The session is intact and the server has
      // refused it on purpose; dropping to the sign-in form would say nothing
      // and offer the one thing that cannot help. Keep whoever was on screen
      // (an open tab) and let the loader go (a reload, where nobody is yet).
      const refused = (
        error as {
          response?: { status?: number; data?: { code?: string } };
        } | null
      )?.response;
      if (
        refused?.status === 403 &&
        refused.data?.code === ORG_SUSPENDED_CODE
      ) {
        setOrgSuspended(true);
        setAuthStatus((status) => (status === 'loading' ? 'anon' : status));
      } else if (
        refused?.status === 403 &&
        refused.data?.code === ACCOUNT_CLOSING_CODE
      ) {
        setAccountClosing(true);
        setAuthStatus((status) => (status === 'loading' ? 'anon' : status));
      } else {
        clearAuth();
      }
    } finally {
      probing.current = false;
    }
  };

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line
  }, []);

  // Re-derive from the server whenever ANOTHER tab reports a session change:
  // a sign-in, a sign-out, an invite accepted, a password reset. The tab either
  // adopts the new identity or drops to anonymous — it never keeps showing the
  // previous one.
  useEffect(() => {
    const resync = () => {
      checkAuth();
    };

    if (authChannel) {
      authChannel.addEventListener('message', resync);

      return () => authChannel.removeEventListener('message', resync);
    }

    const onStorage = (e: StorageEvent) => {
      if (e.key === 'lobbero-auth-ping') resync();
    };
    window.addEventListener('storage', onStorage);

    return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line
  }, [authChannel]);

  // The interceptor fires this when a refresh fails (expired, revoked, or a
  // reused refresh token). One listener means every screen reacts the same way.
  useEffect(() => {
    const onSessionEnded = () => {
      // SAY WHY. This is somebody who was signed in a moment ago and is about
      // to be looking at the sign-in form: their session was ended somewhere
      // else (another device, an administrator, a password change) or ran out.
      // Said once — every request that was in flight fails the same refresh
      // and fires this event, and only the first finds anybody signed in.
      if (signedInRef.current) {
        signedInRef.current = false;
        toast(SESSION_ENDED_NOTICE);
      }
      clearAuth();
      // DELIBERATELY NOT announced. This event is a CONSEQUENCE of the session
      // being gone, not a change this tab made — the tab that actually ended it
      // already announced, and every other tab discovers the same 401 on its own.
      // Announcing here made two tabs echo: A's failed refresh told B to resync,
      // B's resync failed the same way and told A, and the pair ground through a
      // round trip every ~10ms until one of them navigated. Only DELIBERATE local
      // changes (signIn / endSession / logout) announce.
      const path = window.location.pathname;
      if (path !== '/' && !isPublicPath(path)) {
        navigate('/');
      }
    };

    window.addEventListener(SESSION_ENDED_EVENT, onSessionEnded);

    return () =>
      window.removeEventListener(SESSION_ENDED_EVENT, onSessionEnded);
  }, [navigate, clearAuth]);

  // The interceptor fires this on any request refused with ORG_SUSPENDED: the
  // tab that was open when Lobbero suspended the organisation.
  useEffect(() => {
    const onSuspended = () => setOrgSuspended(true);

    window.addEventListener(ORG_SUSPENDED_EVENT, onSuspended);

    return () => window.removeEventListener(ORG_SUSPENDED_EVENT, onSuspended);
  }, []);

  // Independent Stays IS8: any request refused with ACCOUNT_CLOSING.
  useEffect(() => {
    const onClosing = () => setAccountClosing(true);

    window.addEventListener(ACCOUNT_CLOSING_EVENT, onClosing);

    return () => window.removeEventListener(ACCOUNT_CLOSING_EVENT, onClosing);
  }, []);

  // The public half of checkAuth. `probing` already de-duplicates a concurrent
  // call, so a screen that refreshes on mount cannot double-fetch against the
  // provider's own boot probe.
  const refreshAuth = useCallback(async () => {
    await checkAuth();
    // eslint-disable-next-line
  }, []);

  const endSession = useCallback(async () => {
    try {
      await axios.post(`${apiRoute}/logout`);
    } catch {
      // Even if the call fails, drop local state — the cookies are httpOnly and
      // the server-side session is the real source of truth. A 401 here just
      // means there was nothing to revoke, which is the outcome we wanted.
    }
    clearAuth();
    announceAuthChange();
  }, [clearAuth, announceAuthChange]);

  const logout = useCallback(
    async (everywhere = false, opts: { sessionEnded?: boolean } = {}) => {
      /* HAND THE LOCK BACK BEFORE LEAVING.
       *
       * A push subscription belongs to the BROWSER, not to the session, so it
       * survives sign-out. On a shared front-desk tablet that means the person
       * who signed out keeps receiving parcel and visitor notifications — names
       * and unit numbers — on a device now in front of somebody else, and the
       * next person is told push is already enabled and gets nothing.
       *
       * Best-effort and never blocking: if this fails, the session still ends.
       * The probe in notification-preferences also re-assigns an endpoint it
       * finds, so the two halves cover each other. */
      await releasePushSubscription();

      try {
        // sessionEnded: the server already ended it (closing the account
        // signs everyone out), so a call would only be refused.
        if (!opts.sessionEnded) {
          await axios.post(
            `${apiRoute}${everywhere ? '/auth/logout-all' : '/logout'}`
          );
        }
      } catch {
        // Even if the call fails, drop local state — the cookies are httpOnly and
        // the server-side session is the real source of truth.
      }
      clearAuth();
      announceAuthChange();
      navigate('/');
    },
    [navigate, clearAuth, announceAuthChange]
  );

  const signIn = useCallback(
    (
      nextUser: User,
      nextOnboarding: Onboarding | null,
      nextPermissions: string[]
    ) => {
      setUser(nextUser);
      setOnboarding(nextOnboarding);
      setPermissions(nextPermissions || []);
      setAuthStatus('authed');
      // A different identity now owns this browser's cookies — every other tab
      // must stop rendering the previous one.
      announceAuthChange();
    },
    [announceAuthChange]
  );

  const authContextValue = useMemo<AuthContextType>(
    () => ({
      setUser,
      signIn,
      user,
      onboarding,
      setOnboarding,
      permissions,
      setPermissions,
      authStatus,
      orgSuspended,
      accountClosing,
      setAccountClosing,
      logout,
      endSession,
      refreshAuth,
      cookies,
    }),
    [
      signIn,
      user,
      onboarding,
      permissions,
      authStatus,
      orgSuspended,
      accountClosing,
      logout,
      endSession,
      refreshAuth,
      cookies,
    ]
  );

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};
